import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash';
import {
  saveSelectedKPI,
  saveAndApplySelectedKPI,
  updateCustomKPI,
  deleteCustomKPI,
  getSPIThresholds,
  getKpiMetaConfigs,
  getSelectedKPI,
} from '@src/api/SPI';
import kpiStore from '@src/globalStore/kpiStore';
import { currentKpiIdAtom } from '@src/atoms/panels';
import { isAppSPIEnabledSelector } from '@store/common/selectors';
import { isAdModule, isEiModule, isAppModule, isOdpModule } from '@src/utils/constants';
import { checkFeature } from '@utils/platform/features/checkFeature';

export const DEFAULT_KPI_ID = isAppModule ? 5 : 1;

// load and save selected kpi for user
// the selected kpi is prefetched and cached(on client and server) as part useInitialization
function useKPI() {
  const setKPIs = kpiStore((state) => state.setKPIs);
  const setKpiMetaConfigs = kpiStore((state) => state.setKpiMetaConfigs);
  const queryClient = useQueryClient();
  const isAppSPIEnabled = useSelector(isAppSPIEnabledSelector);
  const [currentKpiId, setCurrentKpiId] = useAtom(currentKpiIdAtom);

  let kpiEnabled = false;
  if (isAdModule || isOdpModule) {
    kpiEnabled = false;
  } else if (isAppModule) {
    kpiEnabled = isAppSPIEnabled;
  } else if (isEiModule) {
    kpiEnabled = checkFeature('common.kpi', true);
  }

  const { data: kpiList, isLoading: isKpiListLoading } = useQuery(
    ['kpi-list'],
    () => getSPIThresholds({}),
    {
      retry: true,
      enabled: kpiEnabled,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 min
      select: (response) => get(response, ['data'], {}),
    },
  );

  const { data: kpiMetaConfigs } = useQuery(['kpi-meta-configs'], () => getKpiMetaConfigs({}), {
    retry: true,
    enabled: kpiEnabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 min
    select: (response) => get(response, ['data'], {}),
  });

  const forceUpdateCurrentKpi = () => {
    queryClient.invalidateQueries(['selected-kpi']);
  };

  const forceUpdateKpiList = () => {
    queryClient.invalidateQueries(['kpi-list']);
  };

  const forceUpdateKpiMetaConfigs = () => {
    queryClient.invalidateQueries(['kpi-meta-configs']);
  };

  // get selected kpi for user
  const { data: selectedKpi, isLoading: isSelectedKpiLoading } = useQuery(
    ['selected-kpi'],
    () => getSelectedKPI({}),
    {
      retry: true,
      enabled: kpiEnabled,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      select: (response) => get(response, ['data'], {}),
    },
  );

  // Sync list to store and ensure currentKpiId is valid so useCurrentKpiConfig returns the right config
  useEffect(() => {
    const list = kpiList?.kpiThresholds;
    if (!list?.length || isSelectedKpiLoading) return;

    const existsInList = list.some((k: any) => k.kpiID === currentKpiId);
    const storeList = kpiStore.getState().kpiList;
    const existsInStore = storeList?.some((k: any) => k.kpiID === currentKpiId);

    if (currentKpiId == null) {
      setKPIs(list);
      setCurrentKpiId(selectedKpi?.kpiID || DEFAULT_KPI_ID);
      return;
    }
    // Don't overwrite store when current id is in store but not in list (optimistic create – list still stale)
    if (existsInList || !existsInStore) {
      setKPIs(list);
    }
    // Fallback to default only when selection is missing from both list and store (e.g. deleted)
    if (!existsInList && !existsInStore) {
      setCurrentKpiId(DEFAULT_KPI_ID);
    }
  }, [kpiList, currentKpiId, selectedKpi, setKPIs, setCurrentKpiId]);

  useEffect(() => {
    if (kpiMetaConfigs) {
      setKpiMetaConfigs(kpiMetaConfigs);
    }
  }, [kpiMetaConfigs, setKpiMetaConfigs]);

  // save user selection, invalidate cache on success
  const applyKpiMutation = useMutation(
    (kpiId: number) => {
      return saveSelectedKPI({ kpiId });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['selected-kpi']);
      },
    },
  );

  const saveKpiThresholdsMutation = useMutation(
    (thresholds) => {
      return saveAndApplySelectedKPI(thresholds);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kpi-list']);
        queryClient.invalidateQueries(['selected-kpi']);
      },
    },
  );

  const updateKpiThresholdsMutation = useMutation(
    (thresholds) => {
      return updateCustomKPI(thresholds);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kpi-list']);
        queryClient.invalidateQueries(['selected-kpi']);
      },
    },
  );

  const deleteKpiMutation = useMutation(
    (kpiId: number) => {
      return deleteCustomKPI({ kpiId });
    },
    {
      onSuccess: (_data, deletedKpiId: number) => {
        if (deletedKpiId === currentKpiId) {
          setCurrentKpiId(DEFAULT_KPI_ID);
        }
        queryClient.invalidateQueries(['kpi-list']);
        queryClient.invalidateQueries(['selected-kpi']);
      },
    },
  );

  return {
    applyKpiMutation,
    saveKpiThresholdsMutation,
    updateKpiThresholdsMutation,
    deleteKpiMutation,
    isKpiListLoading,
    forceUpdateCurrentKpi,
    forceUpdateKpiList,
    forceUpdateKpiMetaConfigs,
  };
}

export default useKPI;
